/**
 * CSRF Token Manager
 *
 * Manages CSRF tokens to prevent Cross-Site Request Forgery attacks.
 * The token is generated client-side and sent with all state-changing requests.
 */

const CSRF_TOKEN_KEY = 'csrf_token';
const CSRF_TOKEN_HEADER = 'X-CSRF-Token';

class CSRFTokenManager {
  private token: string | null = null;

  /**
   * Generate a cryptographically secure random token
   */
  private generateToken(): string {
    // Use Web Crypto API for secure random generation
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Get or create CSRF token
   */
  getToken(): string {
    // Check memory cache first
    if (this.token) {
      return this.token;
    }

    // Check sessionStorage
    let token = sessionStorage.getItem(CSRF_TOKEN_KEY);

    // If no token exists, generate a new one
    if (!token) {
      token = this.generateToken();
      this.setToken(token);
    }

    this.token = token;
    return token;
  }

  /**
   * Set a new CSRF token
   */
  setToken(token: string): void {
    this.token = token;
    sessionStorage.setItem(CSRF_TOKEN_KEY, token);
  }

  /**
   * Refresh the CSRF token (generate a new one)
   */
  refreshToken(): string {
    const newToken = this.generateToken();
    this.setToken(newToken);
    return newToken;
  }

  /**
   * Clear the CSRF token
   */
  clearToken(): void {
    this.token = null;
    sessionStorage.removeItem(CSRF_TOKEN_KEY);
  }

  /**
   * Get the header name for CSRF token
   */
  getHeaderName(): string {
    return CSRF_TOKEN_HEADER;
  }

  /**
   * Get headers object with CSRF token
   */
  getHeaders(): Record<string, string> {
    return {
      [CSRF_TOKEN_HEADER]: this.getToken()
    };
  }
}

// Export singleton instance
export const csrfTokenManager = new CSRFTokenManager();

// Initialize token on module load
csrfTokenManager.getToken();
